import classNames from 'classnames';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { DraggableCore } from 'react-draggable';
import { useDispatch, batch } from 'react-redux';
import ActionIcon from 'components/ActionIcon';
import { ActionsColor } from 'components/ArtboardLayoutActions/constants';
import { IconType } from 'components/Icon/constants';
import { setIsResizingLayout } from 'containers/App/actions';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { toPx } from 'utils/toPx';
import styles from './styles.module.scss';

type Props = {
  height: number;
  handleDrag: (event: DragEvent) => void;
  handleDragStart?: () => void;
  handleDragStop: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  color?: ActionsColor;
  disabled?: boolean;
  shiftResizeButton?: boolean;
  dataTestId?: string;
};

const ResizeButton: React.FunctionComponent<Props> = (props) => {
  const {
    color = ActionsColor.BLUE,
    handleDrag,
    handleDragStart = _.noop,
    handleDragStop = _.noop,
    height,
    onMouseEnter = _.noop,
    onMouseLeave = _.noop,
    disabled = false,
    shiftResizeButton = false,
    dataTestId = 'resizeBtn',
  } = props;
  const dispatch = useDispatch();

  const onDragStart = useCallback((): void => {
    batch(() => {
      dispatch(saveAppState());
      dispatch(setIsResizingLayout(true));
    });
    handleDragStart();
  }, [dispatch, handleDragStart]);

  const onDragEnd = useCallback((): void => {
    dispatch(setIsResizingLayout(false));
    handleDragStop();
  }, [dispatch, handleDragStop]);

  return (
    <DraggableCore
      onDrag={handleDrag}
      onStart={onDragStart}
      onStop={onDragEnd}
      disabled={disabled}
    >
      <div
        className={classNames(
          styles.ResizeButton,
          { [styles.shiftResizeButton]: shiftResizeButton },
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid={dataTestId}
      >
        <div
          className={styles.bottomAction}
        >
          <div>
            <ActionIcon
              className={styles.resizeIcon}
              backgroundColor={color}
              color="secondary"
              isDisabled={disabled}
              size="badge"
              type={IconType.UP_AND_DOWN_ARROWS}
              dataTestId={`${dataTestId}-actionIcon${IconType.UP_AND_DOWN_ARROWS}`}
            />
          </div>
        </div>
        <div
          className={classNames(
            styles.elementHeight,
            styles[`borderColor_${color}`],
            styles[`backgroundColor_${color}`],
            { [styles.disabled]: disabled },
          )}
        >
          {toPx(height)}
        </div>
      </div>
    </DraggableCore>
  );
};

export default ResizeButton;
